import React, { useEffect, useState } from "react";
import { Link2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { tagOf } from "@/lib/useCpSession";

// Finished preview candidates (locked or not) as pickable images.
const useCandidates = (jobs, open) => {
  const [list, setList] = useState([]);
  const ids = jobs.map((j) => j.id).join(",");
  useEffect(() => {
    if (!open) return;
    Promise.all(jobs.map((j) => api.get(`/generations/${j.id}`).then((r) => r.data).catch(() => null)))
      .then((gs) => setList(gs.flatMap((g, n) => (g?.status === "succeeded" ? (g.output_urls || []).map((u, i) => ({ g, i, u, frame: jobs[n].frame })) : []))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ids]);
  return list;
};

export default function ContinuityPicker({ k, st, stem, onChange, onUploaded, max = 2, locked: lockedIn, jobs }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const cont = k.cont || [];
  const locked = (lockedIn || Object.entries(st.locked || {}).map(([f, r]) => ({ frame: Number(f), ref: r }))).filter((l) => l.frame !== k.frame);
  const cands = useCandidates(jobs || st.preview_jobs || [], open);
  const has = (id) => cont.some((r) => r.id === id);
  const full = cont.length >= max;
  const add = (r) => { if (has(r.id)) return; if (full) { toast.error(`Up to ${max} continuity reference${max > 1 ? "s" : ""} for this model`); return; } onChange([...cont, r]); };
  const fromGen = async (c) => {
    const key = `${c.g.id}-${c.i}`;
    setBusy(key);
    try {
      const { data } = await api.post("/references/from-generation", { generation_id: c.g.id, index: c.i, name: `${stem}_${c.frame}_Continuity_${c.g.id.slice(0, 4)}${c.i + 1}.png` });
      onUploaded?.(data);
      add(data);
    } catch (e) { toast.error(errMsg(e, "Could not tag that image")); } finally { setBusy(""); }
  };
  const lockedUrls = new Set(locked.map((l) => l.ref.url));
  return (
    <div className="mt-2" data-testid={`cg-cont-${k.frame}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid={`cg-cont-open-${k.frame}`} className="h-7 px-2 rounded-md border border-[#22C55E]/50 text-[11px] text-[#7AF25F] flex items-center gap-1 hover:bg-[#22C55E]/10">
              <Link2 className="w-3.5 h-3.5" />Continuity reference</button>
          </PopoverTrigger>
          <PopoverContent className="w-80 bg-[#0E1114] border-zinc-800 text-zinc-100 p-2 max-h-96 overflow-y-auto" data-testid={`cg-cont-menu-${k.frame}`}>
            <div className="text-[11px] text-zinc-400 mb-1.5">Tag an image — colour, lighting and look will be matched to it · {cont.length}/{max}</div>
            {!locked.length && !cands.length && <p className="text-[11px] text-zinc-500">No previews yet — generate one first.</p>}
            <div className="grid grid-cols-3 gap-1.5">
              {locked.map((l) => (
                <button key={l.ref.id} type="button" onClick={() => add(l.ref)} disabled={has(l.ref.id) || full} data-testid={`cg-cont-locked-${k.frame}-${l.frame}`}
                  className="relative rounded border border-[#7AF25F]/50 overflow-hidden disabled:opacity-40">
                  <img src={mediaUrl(l.ref.url)} alt="" className="h-14 w-full object-cover" />
                  <span className="absolute bottom-0 left-0 rounded-tr bg-black/70 px-1 text-[8px] text-[#7AF25F]">#{l.frame} locked</span>
                </button>))}
              {cands.filter((c) => !lockedUrls.has(c.u)).map((c) => (
                <button key={`${c.g.id}-${c.i}`} type="button" onClick={() => fromGen(c)} disabled={!!busy || full} data-testid={`cg-cont-cand-${k.frame}-${c.g.id}-${c.i}`}
                  className="relative rounded border border-zinc-700 overflow-hidden disabled:opacity-50">
                  <img src={mediaUrl(c.u)} alt="" className="h-14 w-full object-cover" />
                  <span className="absolute bottom-0 left-0 rounded-tr bg-black/70 px-1 text-[8px] text-white">#{c.frame}</span>
                  {busy === `${c.g.id}-${c.i}` && <span className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="w-4 h-4 animate-spin" /></span>}
                </button>))}
            </div>
          </PopoverContent>
        </Popover>
        {cont.map((r) => (
          <span key={r.id} className="flex items-center gap-1 rounded border border-[#22C55E]/40 bg-[#22C55E]/10 pl-0.5 pr-1 py-0.5" data-testid={`cg-cont-tag-${k.frame}-${r.id}`}>
            <img src={mediaUrl(r.url)} alt="" className="h-5 w-8 object-cover rounded" />
            <span className="text-[10px] font-mono text-[#7AF25F] max-w-[140px] truncate">@{tagOf(r)}</span>
            <button type="button" onClick={() => onChange(cont.filter((x) => x.id !== r.id))} className="text-zinc-400 hover:text-red-400" data-testid={`cg-cont-remove-${k.frame}-${r.id}`}><X className="w-3 h-3" /></button>
          </span>))}
      </div>
    </div>
  );
}
